import { useCallback, useEffect, useState } from 'react'

import { Separator } from 'components'
import { ActionIconEnum } from 'enums/ActionIcon'
import { PageTitles } from 'enums/PageTitles'
import { TagEventsEnum } from 'enums/TagEvents'
import { useNavBar } from 'hooks/useNavBar'
import { useHistory } from 'react-router-dom'
import { TypesRoutes } from 'routes/mixedRoutes/types'
import useBoundState from 'store'
import { convertStrToDate } from 'utils/date'
import { AfterSalesStatusEnum } from 'enums/AfterSalesStatus'
import { Icons } from 'utils/icons'

import { Babi } from '@interco/inter-ui/components/Babi'
import { Alert } from '@interco/inter-ui/components/Alert'
import { Text } from '@interco/inter-ui/components/Text'
import { BottomSheet } from '@interco/inter-ui/components/BottomSheet'
import { Button } from '@interco/inter-ui/components/Button'
import { Flex } from '@interco/inter-ui/components/Flex'

import { AccordionsPetDetails } from './AccordionsPetDetails/AccordionsPetDetails'
import * as S from './AfterSalesDetails.styles'
import { PetDetails } from './PetDetails'
import { PetPlanCard } from './PetPlanCard'
import { PrintPetPlanCard } from './PrintPetPlanCard'

export const AfterSalesDetails = () => {
  const setUpdatedAfterSalesPet = useBoundState((state) => state.setUpdatedAfterSalesPet)
  const afterSales = useBoundState((state) => state.afterSales)
  const afterSalesPet = useBoundState((state) => state.afterSalesPet)
  const appInfo = useBoundState((state) => state.appInfo)

  const history = useHistory()
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const [isOpenPaymentChange, setIsOpenPaymentChange] = useState(false)
  const [hideShare, setHideShare] = useState(false)

  const hideCancelation =
    (afterSalesPet.status === AfterSalesStatusEnum.ACTIVE && !!afterSalesPet.cancelDate) ||
    afterSalesPet.status === AfterSalesStatusEnum.CANCELED

  const showCancelationAlert =
    afterSalesPet.status === AfterSalesStatusEnum.ACTIVE && !!afterSalesPet.cancelDate

  const showQuickAccess = afterSalesPet.status === AfterSalesStatusEnum.ACTIVE

  useNavBar({
    navbarTitle: PageTitles.PET,
    actionIcons: {
      leftIcon: ActionIconEnum.BACK,
    },
  })

  const redirectAfterCancelation = useCallback(() => {
    // Atualmente o cancelamento está sendo feito via url da Central de
    // assinaturas no Marketplace, porém ao retornar os dados se perdem.
    // A verificação abaixo leva o usuário para o início da aplicação caso isso aconteça.
    if (Object.keys(afterSalesPet).length === 0) {
      history.push(TypesRoutes.START)
    }
  }, [afterSalesPet, history])

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      setUpdatedAfterSalesPet(afterSalesPet)
      setIsOpenPaymentChange(false)
    }
  }

  useEffect(() => {
    if (isOpenPaymentChange) {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    } else {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isOpenPaymentChange])

  useEffect(() => {
    redirectAfterCancelation()
  }, [redirectAfterCancelation])

  useEffect(() => {
    setHideShare(!!(appInfo && appInfo.appVersion < '14.2'))
  }, [appInfo])

  const handleOpenBottomSheet = () => {
    setIsBottomSheetOpen(true)
  }

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false)
  }

  const icons = {
    petWallet: <Icons.MoneyCheckBack width={24} height={24} color="var(--primary500)" />,
  }

  return (
    <S.Container>
      <S.SubscriptionDetails>
        <PetDetails hideCancelation={hideCancelation} afterSalesPet={afterSalesPet} />
      </S.SubscriptionDetails>

      {showQuickAccess && (
        <S.QuickAccess>
          <Button variant="link" onClick={handleOpenBottomSheet}>
            <S.QuickAccessIcon>{icons.petWallet}</S.QuickAccessIcon>
            <Text variant="caption-2" bold colorWeight={500}>
              Carteirinha
            </Text>
          </Button>
        </S.QuickAccess>
      )}

      {showCancelationAlert && afterSalesPet.cancelDate && (
        <S.CancelationAlert>
          <Alert title="Solicitação de Cancelamento recebida" type="grayscale">
            Seu plano será encerrado em {convertStrToDate(afterSalesPet.cancelDate)}. Mas não se
            preocupe, seu Pet continuará protegido até lá.
          </Alert>
        </S.CancelationAlert>
      )}

      <Separator margin="32px 0 32px 0" />
      <AccordionsPetDetails
        setIsOpenPaymentChange={setIsOpenPaymentChange}
        urlPdfGracePeriods={afterSales.urlPdfGracePeriods}
        afterSalesPet={afterSalesPet}
        hideCancelationAccordion={hideCancelation}
      />
      <Separator margin="32px 0" />
      <S.Footer>
        <Babi origin={TagEventsEnum.PRODUCT} />
      </S.Footer>

      {isBottomSheetOpen && (
        <BottomSheet onClose={handleCloseBottomSheet}>
          <S.BottomSheetContent data-testid="bottom-sheet-details">
            <Flex direction="column" gap="16px" alignItems="center">
              <PetPlanCard
                hideShare={hideShare}
                type={afterSalesPet.type}
                name={afterSalesPet.name}
                status={afterSalesPet.status}
                planName={afterSalesPet.planName}
                socialId={afterSalesPet.socialId}
                petDateBirth={afterSalesPet.petDateBirth}
              />
              <Alert type="grayscale">
                Lembre-se: no Plano Pet do Inter não é necessário microchipar seu pet para usar o
                plano. Basta informar seu CPF no momento da utilização.
              </Alert>
              <Button
                fullWidth
                variant="primary"
                onClick={handleCloseBottomSheet}
                style={{ marginTop: '16px' }}
              >
                Entendi
              </Button>
            </Flex>
          </S.BottomSheetContent>
        </BottomSheet>
      )}
      <PrintPetPlanCard afterSalesPet={afterSalesPet} />
    </S.Container>
  )
}
